import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Share2, Play, Users, Coins, Heart, Gamepad2, Loader2, CalendarDays, Rocket, Rss } from "lucide-react";
import { toPng } from "html-to-image";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const IS_DECEMBER = new Date().getMonth() === 11;
const YEAR = new Date().getFullYear();

export default function CreatorWrapped() {
  const [w, setW] = useState(null);           // month data (drives the trigger teaser)
  const [yearData, setYearData] = useState(null);
  const [scope, setScope] = useState(IS_DECEMBER ? "year" : "month");
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const cardRef = useRef(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    let alive = true;
    api.get("/growth/wrapped").then((r) => { if (alive) setW(r.data); }).catch(() => setW(false));
    return () => { alive = false; };
  }, []);

  // Lazy-load the year-in-review the first time it's viewed.
  useEffect(() => {
    if (scope === "year" && yearData === null) {
      api.get("/growth/wrapped", { params: { scope: "year" } }).then((r) => setYearData(r.data)).catch(() => setYearData(false));
    }
  }, [scope, yearData]);

  // One-tap deep link from the Wrapped email (/app?wrapped=1) auto-opens the card.
  useEffect(() => {
    if (w && w.has_activity && params.get("wrapped") === "1") {
      setOpen(true);
      params.delete("wrapped"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w]);

  if (!w || w === false || !w.has_activity) return null;

  const active = scope === "year" ? (yearData && yearData !== false ? yearData : null) : w;
  const isYear = scope === "year";
  const periodWord = isYear ? "this year" : "this month";

  const postToFeed = async () => {
    setPosting(true);
    try {
      const { data } = await api.post(`/social/feed/wrapped`, null, { params: { scope } });
      toast.success(data.message || "Posted to your feed! 🎉");
      setPosted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post to the feed.");
    } finally { setPosting(false); }
  };

  const share = async () => {
    if (!cardRef.current) return;
    setSharing(true);    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "gameview-wrapped.png", { type: "image/png" });
      const label = active?.period_label || w.month;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `My ${label} on GameView`, text: `My ${label} creator stats on GameView Studio! 🎮` });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl; a.download = "gameview-wrapped.png"; a.click();
        toast.success("Wrapped card downloaded — share it anywhere! 🎉");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create the card. Please try again.");
    } finally { setSharing(false); }
  };

  const d = active || {};
  const stats = [
    { icon: Play, label: "Plays", value: (d.plays || 0).toLocaleString(), color: "text-sky-400" },
    { icon: Users, label: "New fans", value: (d.new_followers || 0).toLocaleString(), color: "text-fuchsia-400" },
    { icon: Coins, label: "Earned", value: `$${(d.earnings || 0).toFixed(0)}`, color: "text-emerald-400" },
    { icon: Heart, label: "Tips", value: `$${(d.tips || 0).toFixed(0)}`, color: "text-pink-400" },
  ];

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="creator-wrapped-trigger"
        className={`mb-6 w-full text-left rounded-2xl border p-4 sm:p-5 transition-colors group ${IS_DECEMBER
          ? "border-amber-400/50 bg-gradient-to-r from-amber-400/15 via-[#d946ef]/10 to-[#3b9eff]/15 hover:border-amber-400/80"
          : "border-[#7c3aed]/40 bg-gradient-to-r from-[#7c3aed]/15 via-[#d946ef]/10 to-[#3b9eff]/15 hover:border-[#7c3aed]/70"}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-[#7c3aed] to-[#3b9eff] text-white shrink-0">
              {IS_DECEMBER ? <Rocket className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
            </span>
            <div>
              <div className="font-display font-extrabold flex items-center gap-2">
                {IS_DECEMBER ? `Your ${YEAR} Year in Review is ready 🎆` : `Your ${w.month} Wrapped is ready ✨`}
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{(w.plays || 0).toLocaleString()} plays · {(w.new_followers || 0)} new fans · ${(w.earnings || 0).toFixed(0)} earned {IS_DECEMBER ? "this month" : ""} — tap to view & share</div>
            </div>
          </div>
          <span className="text-sm font-semibold text-[hsl(var(--primary))] group-hover:underline shrink-0">View →</span>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="creator-wrapped-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#d946ef]" /> {isYear ? `${YEAR} Year in Review` : `${w.month} Wrapped`}</DialogTitle>
            <DialogDescription>Your {periodWord} at a glance — share it with your community.</DialogDescription>
          </DialogHeader>

          {/* Scope toggle: month vs full-year in-review */}
          <div className="inline-flex rounded-full border border-[hsl(var(--border))] p-0.5 self-start" data-testid="wrapped-scope-toggle">
            <button onClick={() => setScope("month")} data-testid="wrapped-scope-month"
              className={`px-3 py-1 text-xs font-bold rounded-full transition-colors ${!isYear ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}>
              <CalendarDays className="w-3.5 h-3.5 inline mr-1" />This month
            </button>
            <button onClick={() => setScope("year")} data-testid="wrapped-scope-year"
              className={`px-3 py-1 text-xs font-bold rounded-full transition-colors ${isYear ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}>
              <Rocket className="w-3.5 h-3.5 inline mr-1" />{YEAR}
            </button>
          </div>

          {isYear && !active ? (
            <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#d946ef]" /></div>
          ) : (
            <div ref={cardRef} data-testid="creator-wrapped-card"
              className="rounded-2xl p-6 text-white relative overflow-hidden"
              style={{ background: isYear ? "linear-gradient(150deg, #2b1006 0%, #3a1259 45%, #10233f 100%)" : "linear-gradient(150deg, #150a2e 0%, #2a1259 45%, #10233f 100%)" }}>
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-30" style={{ background: `radial-gradient(circle, ${isYear ? "#f59e0b" : "#d946ef"}, transparent 70%)` }} />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/70">
                  <span className="text-lg">🎮</span> GameView {isYear ? "Year in Review" : "Wrapped"}
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <span className="text-4xl">{d.avatar_emoji}</span>
                  <div>
                    <div className="font-display text-xl font-extrabold leading-tight">{d.name}</div>
                    <div className="text-sm text-white/70">{d.period_label || d.month}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-5">
                  {stats.map((s) => (
                    <div key={s.label} className="rounded-xl bg-white/10 backdrop-blur p-3">
                      <s.icon className={`w-4 h-4 ${s.color}`} />
                      <div className="font-display text-2xl font-extrabold mt-1 tabular-nums">{s.value}</div>
                      <div className="text-[11px] text-white/60">{s.label} {periodWord}</div>
                    </div>
                  ))}
                </div>
                {isYear && d.best_month && (
                  <div className="mt-4 rounded-xl bg-white/10 backdrop-blur p-3 flex items-center gap-3" data-testid="wrapped-best-month">
                    <span className="text-2xl">📈</span>
                    <div className="min-w-0"><div className="text-[11px] text-white/60">Your biggest month</div><div className="font-bold">{d.best_month.label}</div></div>
                    <div className="ml-auto text-right shrink-0"><div className="font-display font-extrabold tabular-nums">{d.best_month.plays.toLocaleString()}</div><div className="text-[10px] text-white/60">plays</div></div>
                  </div>
                )}
                {d.top_game && (
                  <div className="mt-4 rounded-xl bg-white/10 backdrop-blur p-3 flex items-center gap-3">
                    <span className="text-2xl">{d.top_game.cover_emoji}</span>
                    <div className="min-w-0">
                      <div className="text-[11px] text-white/60 flex items-center gap-1"><Gamepad2 className="w-3 h-3" /> Top game {periodWord}</div>
                      <div className="font-bold truncate">{d.top_game.title}</div>
                    </div>
                    <div className="ml-auto text-right shrink-0">
                      <div className="font-display font-extrabold tabular-nums">{d.top_game.plays.toLocaleString()}</div>
                      <div className="text-[10px] text-white/60">plays</div>
                    </div>
                  </div>
                )}
                <div className="mt-4 text-center text-[11px] text-white/50">Create your own at gameview.studio</div>
              </div>
            </div>
          )}

          <Button onClick={share} disabled={sharing || (isYear && !active)} data-testid="creator-wrapped-share"
            className="rounded-full w-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] mt-1">
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share my {isYear ? "Year in Review" : "Wrapped"}
          </Button>
          <Button onClick={postToFeed} disabled={posting || posted || (isYear && !active)} variant="outline" data-testid="creator-wrapped-post-feed"
            className="rounded-full w-full">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rss className="w-4 h-4" />} {posted ? "Posted to your feed! 🎉" : "Post to my feed"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
